import { Button } from '@repo/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/command'
import { useT } from '@repo/ui/hooks'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import { cn } from '@repo/ui/utils'
import type { Column } from '@tanstack/react-table'
import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BeneficiaryListDataItem } from '@/components/beneficiary/list/table/Table'
import { setBeneficiaryListColumnVisibility } from '@/store/case-worker-preferences/slice'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

export function ColumnsSelector({
  columns,
}: {
  columns: Column<BeneficiaryListDataItem, unknown>[]
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const [open, setOpen] = useState(false)

  const hideableColumns = columns.filter((column) => column.getCanHide())

  const savedColumnIds = useStoreState(
    (state) => state.caseWorkerPreferences.beneficiaryListColumnIds,
  )

  const handleCheckedChange = (
    column: Column<BeneficiaryListDataItem, unknown>,
    checked: boolean,
  ) => {
    dispatch(setBeneficiaryListColumnVisibility({ columnId: column.id, visible: checked }))
  }

  useEffect(() => {
    columns.forEach((column) => {
      if (savedColumnIds.includes(column.id)) {
        if (!column.getIsVisible()) column.toggleVisibility(true)
      } else if (column.getCanHide()) {
        column.toggleVisibility(false)
      }
    })
  }, [savedColumnIds])
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="data-[state=open]:bg-primary-100 ml-auto">
          {tc('columns', { cfl: true })} <ChevronDown className="size-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder={tc('search', { cfl: true })} className="h-9" />
          <CommandList>
            <CommandEmpty>{tc('result', { cfl: true, count: 0 })}</CommandEmpty>
            <CommandGroup>
              {hideableColumns.map((column) => (
                <Item key={column.id} column={column} onSelect={handleCheckedChange} />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function Item({
  column,
  onSelect,
}: {
  column: Column<BeneficiaryListDataItem, unknown>
  onSelect: (column: Column<BeneficiaryListDataItem, unknown>, checked: boolean) => void
}) {
  // @ts-expect-error we are sure meta and label exist here
  const label = column.columnDef.meta.label as string
  const isChecked = column.getIsVisible()
  return (
    <CommandItem
      key={column.id}
      className={cn('text-sm capitalize', isChecked && 'font-semibold')}
      value={label}
      onSelect={() => onSelect(column, !isChecked)}
    >
      {label}
      <Check className={cn('text-primary-500 ml-auto size-4', isChecked ? 'block' : 'hidden')} />
    </CommandItem>
  )
}
